/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 201906029 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { EmployeeService } from '../employee.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { Employee, EmployeeDTO } from 'src/app/_model/employee';
import { EmployeeDaoService } from '../../dao/employee-dao.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { IMSId, IMSObject } from 'src/app/_model/ims-object';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads App Parameters
 */
@Injectable()
export class EmployeeLogicService implements EmployeeService {
  public employees$ = new BehaviorSubject<Employee[]>(undefined);
  public team$ = new BehaviorSubject<Employee[]>(undefined);
  public me$ = new BehaviorSubject<Employee>(undefined);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private employeeDaoService: EmployeeDaoService,
    private authenticationService: AuthenticationService,
    private appParameterService: AppParameterService,
  ) {
    this.initialize();
  }

  // Resetable
  public async reset() {
    await this.employeeDaoService.reset();
  }

  public async clear() {
    await this.employeeDaoService.clear();
    this.employees$.next(undefined);
    this.team$.next(undefined);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.loadRemoteEmployees(apiLevel, syncKey);
  }
  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalEmployees();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Public Functions
  public getEmployeeById(id: IMSId): Employee {
    if (id === undefined) {
      return undefined;
    }

    let emp = this.employees$.getValue()?.find((emp) => {
      if (emp?.id?.imsId === id?.imsId && emp?.id?.secondaryId === id?.secondaryId) {
        return true;
      }
      return false;
    });
    if (!emp) {
      emp = this.team$.getValue()?.find((emp) => {
        if (
          emp?.id?.imsId === id?.imsId &&
          emp?.id?.secondaryId === id?.secondaryId
        ) {
          return true;
        }
        return false;
      });
    }
    if (!emp) {
      if (
        this.me$.getValue()?.id?.imsId === id?.imsId &&
        this.me$.getValue()?.id?.secondaryId === id?.secondaryId
      ) {
        emp = this.me$.getValue();
      }
    }
    return emp;
  }

  public async addToTeam(employee: Employee): Promise<void> {
    try {
      await this.employeeDaoService.addToTeam(employee);
      await this.loadLocalEmployees();
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error adding employee',
        employee.label,
        ' to team.',
        error?.message,
      );
      throw error;
    }
  }

  public async removeFromTeam(employee: Employee): Promise<void> {
    try {
      await this.employeeDaoService.removeFromTeam(employee);
      await this.loadLocalEmployees();
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error adding employee',
        employee.label,
        ' to team.',
        error?.message,
      );
      throw error;
    }
  }

  public async updateAny(employeeDtos: EmployeeDTO[]): Promise<void> {
    const employees: Employee[] = [];
    const team: Employee[] = [];

    const curTeam = this.team$.getValue() ?? [];
    const meId = this.me$.getValue()?.id?.imsId

    for (const employeeDto of employeeDtos ?? []) {
      if (employeeDto?.id === meId) {
        this.logger.log(
          this.constructor.name,
          '- Update me with new data',
          employeeDto,
        );
        await this.employeeDaoService.saveMe(
          Employee.fromDto(employeeDto)
        );
      } else {
        if (curTeam.findIndex(emp => emp.id.imsId === employeeDto.id) !== -1) {
          this.logger.log(
            this.constructor.name,
            '- Update team member with new data',
            employeeDto,
          );
          team.push(Employee.fromDto(employeeDto));
        } else {
          this.logger.log(
            this.constructor.name,
            '- Update employee with new data',
            employeeDto,
          );
          employees.push(Employee.fromDto(employeeDto));
        }
      }
    } 


    await this.employeeDaoService.saveTeam(team);

    await this.employeeDaoService.saveEmployees(employees);
    await this.loadLocalEmployees();
  }

  public getTeamIDs(): IMSId[] {
    let teamIds: IMSId[] = [];
    for (const emp of this.team$.getValue()) {
      teamIds.push(emp?.id);
    }
    return teamIds;
  }

  public async updateTeam(empIds: IMSId[]): Promise<void> {
    this.logger.log(
      this.constructor.name,
      '- Received new Team from external component',
      empIds,
    );

    this.logger.log(
      this.constructor.name,
      '- looking for empIds',
      empIds,
      'in local Emps',
      this.employees$.getValue(),
    );

    for (const empId of empIds) {
      // Do not add me to the team as i am always in my team
      if (this.me$.getValue()?.id?.imsId !== empId?.imsId) {
        let emp = this.getEmployeeById(empId);

        if (emp) {
          await this.addToTeam(emp);
        } else {
          this.logger.error(
            this.constructor.name,
            '- Could not find employee with id',
            empId,
            'in employees',
            this.employees$.getValue(),
          );
        }
      }
    }

    const empIdsSearchable = empIds.map((it) => it.imsId);
    for (const member of this.team$.getValue()) {
      if (!empIdsSearchable.includes(member?.id?.imsId)) {
        this.logger.log(
          this.constructor.name,
          '- employee',
          member?.id?.imsId,
          'should no longer be in team',
          empIdsSearchable,
          'Remove him from team',
        );
        await this.removeFromTeam(member);
      }
    }

    this.logger.log(
      this.constructor.name,
      'Synced team:',
      this.team$.getValue(),
    );
  }

  public onlyMeInTeam(): boolean {
    if (this.team$.getValue()?.length !== 1) {
      return false;
    }

    const me = this.me$.getValue();
    return this.getTeamIDs().reduce((prev, cur) => {
      return prev && IMSObject.equals(cur, me?.id);
    }, true);
  }

  // Private Functions

  private async loadLocalEmployees() {
    try {
      this.logger.log(this.constructor.name, '- Load local Employees');
      let employess: Employee[] = await this.employeeDaoService.getEmployees();
      let team: Employee[] = await this.employeeDaoService.getTeam();
      let me: Employee = await this.employeeDaoService.getMe();

      this.logger.log(
        this.constructor.name,
        'loadLocalEmployees next employees',
        employess,
      );
      this.employees$.next(employess);
      this.logger.log(
        this.constructor.name,
        'loadLocalEmployees next team',
        team,
      );
      this.team$.next(team);
      this.logger.log(this.constructor.name, 'loadLocalEmployees next me', me);
      this.me$.next(me);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Employees: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteEmployees(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        '- Load remote Employees from IMS',
      );

      try {
        let employees: Employee[] = [];
        let employeesDto: EmployeeDTO[] =
          await this.remoteActionService.sendRemoteAction(
            new RemoteAction({
              endpoint: 'syncemployees',
              cachable: false,
              fifo: false,
              authenticationRequired: true,
              data: {
                synckey: syncKey
              }
            }),
          );

        for (const employeeDto of employeesDto) {
          employees.push(Employee.fromDto(employeeDto));
        }

        this.logger.log(
          this.constructor.name,
          'Downloaded Employees: ',
          employees.length,
        );
        let me: Employee;
        employees = employees.filter((employee) => {
          if (
            employee.appusername.toLocaleLowerCase() ===
            this.authenticationService.getUserName().toLocaleLowerCase()
          ) {
            me = employee;
          }
          return employee;
        });

        if (
          this.appParameterService.appParameters$
            .getValue()
            ?.getBooleanParameter(
              'imsmc.teamchooser.basedonassignedsvemp',
              false,
            )
        ) {
          await this.buildTeamBasedOnAssignedSvEmp(employees, me);
        } else {
          // Check if loaded employees are already in the team then keep
          // them in the team but always use the loaded one (updates etc.)
          const oldTeam = await this.employeeDaoService.getTeam();

          const newTeam: Employee[] = [];
          if (oldTeam) {
            for (const oldEmp of oldTeam) {
              const newEmp = employees.find((emp) => {
                return IMSObject.equals(emp?.id, oldEmp?.id);
              });

              // Team Member was found in the loaded employees
              if (newEmp) {
                employees = employees.filter((emp) => {
                  return emp?.id !== newEmp?.id ? emp : undefined;
                });

                newTeam.push(newEmp);
              }
            }
          }

          // If team is empty at least the appuser needs to be in the team
          if (newTeam.length === 0) {
            this.logger.log(
              this.constructor.name,
              'New Team is empty, fill with app user',
            );
            newTeam.push(me);
            employees = employees.filter((emp) => {
              return emp?.id !== me?.id;
            });
          }

          await this.employeeDaoService.clear();
          this.logger.log(
            this.constructor.name,
            'loadRemoteEmployees save employees',
            employees,
          );
          await this.employeeDaoService.saveEmployees(employees);
          this.logger.log(
            this.constructor.name,
            ' loadRemoteEmployees save team',
            newTeam,
          );
          await this.employeeDaoService.saveTeam(newTeam);

          this.logger.log(
            this.constructor.name,
            'loadRemoteEmployees next employees',
            employees,
          );
          this.employees$.next(employees);
          this.logger.log(
            this.constructor.name,
            ' loadRemoteEmployees next team',
            newTeam,
          );
          this.team$.next(newTeam);
        }

        this.logger.log(
          this.constructor.name,
          ' loadRemoteEmployees save me',
          me,
        );
        await this.employeeDaoService.saveMe(me);
        this.logger.log(
          this.constructor.name,
          'loadRemoteEmployees next me',
          me,
        );
        this.me$.next(me);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading Employees: ',
          error?.message,
        );
        throw error;
      }
    }
  }

  async initializeCompleted(): Promise<void> {}

  private async buildTeamBasedOnAssignedSvEmp(
    employees: Employee[],
    me: Employee,
  ) {
    this.logger.log(
      this.constructor.name,
      'buildTeamBasedOnAssignedSvEmp myID',
      me.getImsId(),
    );

    const teamLeaderId = me.assignedSvEmp?.id ? me.assignedSvEmp?.id : me?.id;
    this.logger.log(
      this.constructor.name,
      'buildTeamBasedOnAssignedSvEmp teamLeader Id',
      teamLeaderId,
    );

    const team: Employee[] = [];
    const notTeam: Employee[] = [];
    for (const emp of employees) {
      if (
        IMSObject.equals(emp?.id, teamLeaderId) ||
        IMSObject.equals(emp.assignedSvEmp?.id, teamLeaderId) ||
        IMSObject.equals(emp?.id, me?.id)
      ) {
        team.push(emp);
      } else {
        notTeam.push(emp);
      }
    }
    this.logger.log(
      this.constructor.name,
      'buildTeamBasedOnAssignedSvEmp team',
      team,
    );
    this.logger.log(
      this.constructor.name,
      'buildTeamBasedOnAssignedSvEmp employees',
      notTeam,
    );

    await this.employeeDaoService.clear();
    this.logger.log(
      this.constructor.name,
      'loadRemoteEmployees save employees',
      notTeam,
    );
    await this.employeeDaoService.saveEmployees(notTeam);
    this.logger.log(
      this.constructor.name,
      ' loadRemoteEmployees save team',
      team,
    );
    await this.employeeDaoService.saveTeam(team);

    this.logger.log(
      this.constructor.name,
      'loadRemoteEmployees next employees',
      notTeam,
    );
    this.employees$.next(notTeam);
    this.logger.log(
      this.constructor.name,
      ' loadRemoteEmployees next team',
      team,
    );
    this.team$.next(team);
  }
}
